import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui.js';

/**
 * The last stop for a render-time exception.
 *
 * React unmounts the whole tree when a render throws and nothing catches it,
 * which on an SPA means a blank white page: the user is left with no nav, no
 * explanation, and no way back except the browser's own reload. A boundary
 * turns that into a screen that says what happened and offers the two moves
 * that actually help.
 *
 * `fallback` exists so the shell can keep its navigation on screen while only
 * the crashed page is replaced — see AppShell.
 */
interface Props {
  children: ReactNode;
  /** Rendered instead of the default full-screen notice. */
  fallback?: (retry: () => void) => ReactNode;
  /**
   * Changes here clear the error. The shell passes the pathname, so navigating
   * away from a page that crashed leaves the crash behind — without it the
   * boundary would hold the error forever and every later route would render
   * the fallback.
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(previous: Props): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept out of the UI on purpose: a component stack means nothing to the
    // business owner reading the screen, and everything to whoever they send
    // the console to.
    console.error('[ui] render failed', error, info.componentStack);
  }

  private readonly retry = (): void => this.setState({ error: null });

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.retry);
    return <CrashScreen error={this.state.error} onRetry={this.retry} />;
  }
}

/**
 * A function component so it can translate. Class components cannot use hooks,
 * and a crash screen in the wrong language is one more thing gone wrong.
 */
export function CrashScreen({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="center-screen">
      <div className="stack crash" role="alert">
        <h1 className="crash__title">{t('errors.crashTitle')}</h1>
        <p className="muted">{t('errors.crashBody')}</p>
        <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Button variant="primary" onClick={onRetry}>
            {t('errors.crashReload')}
          </Button>
          {/* A real navigation, not a router push: whatever state produced the
              crash is discarded with the page. */}
          <Button variant="ghost" onClick={() => window.location.assign('/')}>
            {t('errors.crashHome')}
          </Button>
        </div>
        {import.meta.env.DEV && <pre className="crash__detail">{error.stack ?? error.message}</pre>}
      </div>
    </div>
  );
}
